import Cart from "cart-store";

import { formatMoney } from "utils";

const esc = s => String(s).replace(/[&<>"']/g, c => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
}[c]));

const EXCLUDED_ADDON_TITLES = [ "sample pack", "bundle of two" ];

class CartOfferPrompts extends HTMLElement {
    connectedCallback() {
        try {
            this.config = JSON.parse(this.dataset.config);
        } catch (e) {
            return;
        }
        this.oilById = new Map(this.config.oil.map(v => [ v.id, v ]));
        this.onClick = this.onClick.bind(this);
        this.addEventListener("click", this.onClick);
        this.unsubscribe = Cart.subscribe(state => this.update(state.cart));
        const current = Cart.getState();
        if (current && current.cart) this.update(current.cart);
    }
    disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        this.removeEventListener("click", this.onClick);
    }
    money(cents) {
        return formatMoney(Math.round(cents), window.theme.money_format);
    }
    tierPercent(variant, qty) {
        if (qty < 2) return 0;
        if (qty === 2) return this.config.tier2;
        return variant.large ? this.config.tier3Large : this.config.tier3Small;
    }
    update(cart) {
        if (!cart || !cart.items || !cart.items.length) {
            this.hidden = true;
            this.innerHTML = "";
            return;
        }
        const c = this.config;
        const retail = cart.items.filter(i => i.product_type === c.oilType && this.oilById.has(i.variant_id) && !this.oilById.get(i.variant_id).bulk);
        const addonLines = cart.items.filter(i => i.product_type === c.addonType && !EXCLUDED_ADDON_TITLES.some(t => (i.variant_title || "").toLowerCase().includes(t)));
        const inCart = new Set(addonLines.map(i => i.product_id));
        const parts = [];
        // 1. Next quantity tier on an emu oil size.
        const upgradable = retail.filter(i => i.quantity < 3);
        const target = upgradable.find(i => i.quantity === 2) || upgradable[0];
        if (target) {
            const v = this.oilById.get(target.variant_id);
            const nextQty = target.quantity + 1;
            const pct = this.tierPercent(v, nextQty);
            const each = v.price * (1 - pct / 100);
            if (pct > 0 && v.available) {
                parts.push(`<div class="cop__group"><p class="cop__msg">Add 1 more <strong>${esc(v.label)}</strong> to save <strong>${pct}%</strong> <span class="cop__soft">(${this.money(each)} each)</span></p>` + `<button type="button" class="cop__btn" data-add="${v.id}" data-qty="1">Add 1 more</button></div>`);
            }
        }
        // 2. Add-on discount.
        const free = c.addons.filter(a => !inCart.has(a.productId));
        if (retail.length && inCart.size < 2 && free.length) {
            const pct = inCart.size === 0 ? c.addon1 : c.addon2;
            const msg = inCart.size === 0 ? `Add a skincare favourite and save <strong>${c.addon1}%</strong>` : `Add one more and both get <strong>${c.addon2}%</strong> off`;
            const shownPct = inCart.size === 0 ? c.addon1 : c.addon2;
            const chips = free.slice(0, 3).map(a => `<button type="button" class="cop__chip" data-add="${a.id}" data-qty="1">` + (a.image ? `<img src="${esc(a.image)}" alt="" width="40" height="40" loading="lazy">` : "") + `<span class="cop__chip-text"><span class="cop__chip-name">${esc(a.name)}</span><span class="cop__chip-price"><s>${this.money(a.price)}</s> <b>${this.money(a.price * (1 - shownPct / 100))}</b></span></span>` + `<span class="cop__chip-add">Add</span></button>`).join("");
            parts.push(`<div class="cop__group"><p class="cop__msg">${msg}</p><div class="cop__chips">${chips}</div></div>`);
        } else if (!retail.length && addonLines.length) {
            // Skincare in the cart but no emu oil yet: offer the oil.
            const sizes = c.oil.filter(v => !v.bulk && v.available);
            if (sizes.length) {
                const pct = c.addon1;
                const buttons = sizes.map(v => `<button type="button" class="cop__btn cop__btn--small" data-add="${v.id}" data-qty="1">${esc(v.label)} · ${this.money(v.price)}</button>`).join("");
                parts.push(`<div class="cop__group"><p class="cop__msg">Add pure emu oil and save <strong>${pct}%</strong> on your skincare</p><div class="cop__row">${buttons}</div></div>`);
            }
        }
        if (!parts.length) {
            this.hidden = true;
            this.innerHTML = "";
            return;
        }
        this.hidden = false;
        this.innerHTML = `<div class="cop__box" role="region" aria-label="Ways to Save"><p class="cop__title">Ways to Save</p>${parts.join("")}<p class="cop__error" role="alert" hidden></p></div>`;
    }
    async onClick(e) {
        const btn = e.target.closest("[data-add]");
        if (!btn || btn.disabled) return;
        const id = Number(btn.dataset.add);
        const qty = Number(btn.dataset.qty || 1);
        const buttons = this.querySelectorAll("button");
        buttons.forEach(b => b.disabled = true);
        btn.setAttribute("aria-busy", "true");
        try {
            await Cart.add([ {
                id: id,
                quantity: qty
            } ]);
        } catch (err) {
            const box = this.querySelector(".cop__error");
            if (box) {
                box.textContent = err.message || "Could not add that. Please try again.";
                box.hidden = false;
            }
            buttons.forEach(b => b.disabled = false);
        }
    }
}

if (!customElements.get("cart-offer-prompts")) customElements.define("cart-offer-prompts", CartOfferPrompts);
